import { supabase } from '../lib/supabase';
import { playerMediaStorage } from './storageService';

class PlayerProfileService {
  constructor() {
    this.tableName = 'player_master_profiles';
  }

  async getAll() {
    const { data, error } = await supabase?.from('player_master_profiles')?.select('*')?.not('user_id', 'is', null)?.eq('verified_status', true)?.order('created_at', { ascending: false });
    
    if (error) throw error;
    return { data: data?.map(item => this.toCamelCase(item)) };
  }

  // Admin method to get ALL players regardless of verification or user_id
  async getAllForAdmin() {
    const { data, error } = await supabase?.from('player_master_profiles')?.select('*')?.order('created_at', { ascending: false });
    
    if (error) throw error;
    return { data: data?.map(item => this.toCamelCase(item)) };
  }

  async getById(id) {
    const { data, error } = await supabase?.from('player_master_profiles')?.select('*')?.eq('id', id)?.single();
    
    if (error) throw error;
    return { data: this.toCamelCase(data) };
  }

  async create(playerData) {
    try {
      // Check if user is authenticated (optional for admin-created profiles)
      const { data: { user }, error: authError } = await supabase?.auth?.getUser();
      
      // If no user is authenticated and no user_id is provided, throw error
      if (!user && !playerData?.userId) {
        throw new Error('User not authenticated');
      }

      let photoPath = null;

      if (playerData?.photoFile) {
        const uploadResult = await playerMediaStorage?.uploadFile(
          playerData?.photoFile,
          'profile-photos'
        );
        
        if (uploadResult?.error) {
          throw new Error(`Photo upload failed: ${uploadResult.error.message}`);
        }
        
        photoPath = uploadResult?.data?.path;
      }

      const insertData = {
        // Use provided user_id if available, otherwise use authenticated user's id, or null for admin-created profiles
        user_id: playerData?.userId || user?.id || null,
        full_name: playerData?.fullName,
        known_as: playerData?.knownAs,
        date_of_birth: playerData?.dateOfBirth || null,
        place_of_birth: playerData?.placeOfBirth,
        nationality_primary: playerData?.nationalityPrimary,
        nationality_secondary: playerData?.nationalitySecondary,
        height: playerData?.height ? parseFloat(playerData?.height) : null,
        weight: playerData?.weight ? parseFloat(playerData?.weight) : null,
        preferred_foot: playerData?.preferredFoot,
        primary_position: playerData?.primaryPosition,
        current_club: playerData?.currentClub,
        current_league: playerData?.currentLeague,
        current_country: playerData?.currentCountry,
        squad_number: playerData?.squadNumber ? parseInt(playerData?.squadNumber) : null,
        contract_type: playerData?.contractType,
        contract_start_date: playerData?.contractStartDate || null,
        contract_end_date: playerData?.contractEndDate || null,
        available_for_transfer: playerData?.availableForTransfer || false,
        available_for_loan: playerData?.availableForLoan || false,
        current_market_value: playerData?.currentMarketValue ? parseFloat(playerData?.currentMarketValue) : null,
        highest_market_value: playerData?.highestMarketValue ? parseFloat(playerData?.highestMarketValue) : null,
        agent_name: playerData?.agentName,
        agency_name: playerData?.agencyName,
        agent_email: playerData?.agentEmail,
        agent_phone: playerData?.agentPhone,
        representation_contract_until: playerData?.representationContractUntil || null,
        verified_status: playerData?.verifiedStatus || false,
        fifa_verified: playerData?.fifaVerified || false,
        profile_visibility: playerData?.profileVisibility || 'public',
        profile_photo: photoPath
      };

      const { data, error } = await supabase?.from(this.tableName)?.insert(insertData)?.select()?.single();

      if (error) throw error;

      return { data: this.toCamelCase(data), error: null };
    } catch (error) {
      console.error('Create player error:', error);
      return { data: null, error };
    }
  }

  async update(id, playerData) {
    try {
      // Authentication is handled at the component/route level
      // Admin users are already authenticated when they reach this point

      let photoPath = playerData?.profilePhoto;

      if (playerData?.photoFile) {
        const uploadResult = await playerMediaStorage?.uploadFile(
          playerData?.photoFile,
          'profile-photos'
        );
        
        if (uploadResult?.error) {
          throw new Error(`Photo upload failed: ${uploadResult.error.message}`);
        }
        
        photoPath = uploadResult?.data?.path;

        if (playerData?.oldPhotoPath) {
          await playerMediaStorage?.deleteFile(playerData?.oldPhotoPath);
        }
      }

      const updateData = {
        full_name: playerData?.fullName,
        known_as: playerData?.knownAs,
        date_of_birth: playerData?.dateOfBirth || null,
        place_of_birth: playerData?.placeOfBirth,
        nationality_primary: playerData?.nationalityPrimary,
        nationality_secondary: playerData?.nationalitySecondary,
        height: playerData?.height ? parseFloat(playerData?.height) : null,
        weight: playerData?.weight ? parseFloat(playerData?.weight) : null,
        preferred_foot: playerData?.preferredFoot,
        primary_position: playerData?.primaryPosition,
        current_club: playerData?.currentClub,
        current_league: playerData?.currentLeague,
        current_country: playerData?.currentCountry,
        squad_number: playerData?.squadNumber ? parseInt(playerData?.squadNumber) : null,
        contract_type: playerData?.contractType,
        contract_start_date: playerData?.contractStartDate || null,
        contract_end_date: playerData?.contractEndDate || null,
        available_for_transfer: playerData?.availableForTransfer || false,
        available_for_loan: playerData?.availableForLoan || false,
        current_market_value: playerData?.currentMarketValue ? parseFloat(playerData?.currentMarketValue) : null,
        highest_market_value: playerData?.highestMarketValue ? parseFloat(playerData?.highestMarketValue) : null,
        agent_name: playerData?.agentName,
        agency_name: playerData?.agencyName,
        agent_email: playerData?.agentEmail,
        agent_phone: playerData?.agentPhone,
        representation_contract_until: playerData?.representationContractUntil || null,
        verified_status: playerData?.verifiedStatus || false,
        fifa_verified: playerData?.fifaVerified || false,
        profile_visibility: playerData?.profileVisibility || 'public',
        profile_photo: photoPath,
        updated_at: new Date()?.toISOString()
      };

      const { data, error } = await supabase?.from(this.tableName)?.update(updateData)?.eq('id', id)?.select()?.single();

      if (error) throw error;

      return { data: this.toCamelCase(data), error: null };
    } catch (error) {
      console.error('Update player error:', error);
      return { data: null, error };
    }
  }

  async delete(id) {
    try {
      // Delete all related records in correct order (child tables first)
      const relatedTables = [
        'player_messages',
        'profile_visitors',
        'visitor_analytics_summary',
        'advanced_performance',
        'competitions',
        'market_value_history',
        'player_media',
        'medical_records',
        'national_team',
        'transfer_history',
        'career_history',
        'performance_stats',
      ];

      for (const table of relatedTables) {
        const { error: relErr } = await supabase?.from(table)?.delete()?.eq('player_id', id);
        if (relErr) {
          // Log but don't stop — table may not have this player's data
          console.warn(`Could not delete from ${table}:`, relErr?.message);
        }
      }

      // Finally delete the master profile
      const { error } = await supabase?.from('player_master_profiles')?.delete()?.eq('id', id);
      if (error) throw error;
      return { success: true, error: null };
    } catch (error) {
      console.error('Delete player error:', error);
      return { success: false, error };
    }
  }

  async getByCurrentUser() {
    const { data: { user } } = await supabase?.auth?.getUser();
    
    if (!user) {
      throw new Error('User not authenticated');
    }
    
    const { data, error } = await supabase
      ?.from('player_master_profiles')
      ?.select('*')
      ?.eq('user_id', user?.id)
      ?.single();
    
    if (error && error?.code !== 'PGRST116') {
      throw error;
    }
    
    return { data: data ? this.toCamelCase(data) : null };
  }

  toCamelCase(obj) {
    if (!obj) return obj;
    return {
      id: obj?.id,
      userId: obj?.user_id,
      fullName: obj?.full_name,
      knownAs: obj?.known_as,
      dateOfBirth: obj?.date_of_birth,
      placeOfBirth: obj?.place_of_birth,
      nationalityPrimary: obj?.nationality_primary,
      nationalitySecondary: obj?.nationality_secondary,
      height: obj?.height,
      weight: obj?.weight,
      preferredFoot: obj?.preferred_foot,
      primaryPosition: obj?.primary_position,
      secondaryPositions: obj?.secondary_positions,
      currentClub: obj?.current_club,
      currentLeague: obj?.current_league,
      currentCountry: obj?.current_country,
      squadNumber: obj?.squad_number,
      contractType: obj?.contract_type,
      contractStartDate: obj?.contract_start_date,
      contractEndDate: obj?.contract_end_date,
      availableForTransfer: obj?.available_for_transfer,
      availableForLoan: obj?.available_for_loan,
      transferListedDate: obj?.transfer_listed_date,
      currentMarketValue: obj?.current_market_value,
      highestMarketValue: obj?.highest_market_value,
      currency: obj?.currency,
      agentName: obj?.agent_name,
      agencyName: obj?.agency_name,
      agentEmail: obj?.agent_email,
      agentPhone: obj?.agent_phone,
      representationContractUntil: obj?.representation_contract_until,
      profileVisibility: obj?.profile_visibility,
      verifiedStatus: obj?.verified_status,
      fifaVerified: obj?.fifa_verified,
      cafVerified: obj?.caf_verified,
      profilePhoto: obj?.profile_photo,
      actionPhotos: obj?.action_photos,
      dataSource: obj?.data_source,
      lastUpdated: obj?.last_updated,
      createdAt: obj?.created_at,
      updatedAt: obj?.updated_at
    };
  }

  toSnakeCase(obj) {
    if (!obj) return obj;
    return {
      user_id: obj?.userId,
      full_name: obj?.fullName,
      known_as: obj?.knownAs,
      date_of_birth: obj?.dateOfBirth,
      place_of_birth: obj?.placeOfBirth,
      nationality_primary: obj?.nationalityPrimary,
      nationality_secondary: obj?.nationalitySecondary,
      height: obj?.height,
      weight: obj?.weight,
      preferred_foot: obj?.preferredFoot,
      primary_position: obj?.primaryPosition,
      secondary_positions: obj?.secondaryPositions,
      current_club: obj?.currentClub,
      current_league: obj?.currentLeague,
      current_country: obj?.currentCountry,
      squad_number: obj?.squadNumber,
      contract_type: obj?.contractType,
      contract_start_date: obj?.contractStartDate,
      contract_end_date: obj?.contractEndDate,
      available_for_transfer: obj?.availableForTransfer,
      available_for_loan: obj?.availableForLoan,
      transfer_listed_date: obj?.transferListedDate,
      current_market_value: obj?.currentMarketValue,
      highest_market_value: obj?.highestMarketValue,
      currency: obj?.currency,
      agent_name: obj?.agentName,
      agency_name: obj?.agencyName,
      agent_email: obj?.agentEmail,
      agent_phone: obj?.agentPhone,
      representation_contract_until: obj?.representationContractUntil,
      profile_visibility: obj?.profileVisibility,
      verified_status: obj?.verifiedStatus,
      fifa_verified: obj?.fifaVerified,
      caf_verified: obj?.cafVerified,
      profile_photo: obj?.profilePhoto,
      action_photos: obj?.actionPhotos,
      data_source: obj?.dataSource
    };
  }
}

export const playerProfileService = new PlayerProfileService();

// Club Career Seasons Service
export const clubCareerService = {
  async getByPlayerId(playerId) {
    const { data, error } = await supabase?.from('club_career_seasons')?.select('*')?.eq('player_id', playerId)?.order('season', { ascending: false });
    
    if (error) throw error;
    return { data: data?.map(item => this.toCamelCase(item)) };
  },

  async create(careerData) {
    const snakeCaseData = this.toSnakeCase(careerData);
    const { data, error } = await supabase?.from('club_career_seasons')?.insert([snakeCaseData])?.select()?.single();
    
    if (error) throw error;
    return { data: this.toCamelCase(data) };
  },

  async update(id, careerData) {
    const snakeCaseData = this.toSnakeCase(careerData);
    const { data, error } = await supabase?.from('club_career_seasons')?.update(snakeCaseData)?.eq('id', id)?.select()?.single();
    
    if (error) throw error;
    return { data: this.toCamelCase(data) };
  },

  async delete(id) {
    const { error } = await supabase?.from('club_career_seasons')?.delete()?.eq('id', id);
    
    if (error) throw error;
    return { success: true };
  },

  toCamelCase(obj) {
    if (!obj) return obj;
    return {
      id: obj?.id,
      playerId: obj?.player_id,
      season: obj?.season,
      clubName: obj?.club_name,
      league: obj?.league,
      country: obj?.country,
      squadNumber: obj?.squad_number,
      contractType: obj?.contract_type,
      contractStartDate: obj?.contract_start_date,
      contractEndDate: obj?.contract_end_date,
      createdAt: obj?.created_at,
      updatedAt: obj?.updated_at
    };
  },

  toSnakeCase(obj) {
    if (!obj) return obj;
    return {
      player_id: obj?.playerId,
      season: obj?.season,
      club_name: obj?.clubName,
      league: obj?.league,
      country: obj?.country,
      squad_number: obj?.squadNumber,
      contract_type: obj?.contractType,
      contract_start_date: obj?.contractStartDate,
      contract_end_date: obj?.contractEndDate
    };
  }
};

// Performance Stats Service
export const performanceStatsService = {
  async getByPlayerId(playerId) {
    const { data, error } = await supabase?.from('performance_stats')?.select('*')?.eq('player_id', playerId)?.order('season', { ascending: false });
    
    if (error) throw error;
    return { data: data?.map(item => this.toCamelCase(item)) };
  },

  async getAllStats() {
    const { data, error } = await supabase?.from('performance_stats')?.select('*')?.order('season', { ascending: false });
    
    if (error) throw error;
    return { data: data?.map(item => this.toCamelCase(item)) };
  },

  async create(statsData) {
    const snakeCaseData = this.toSnakeCase(statsData);
    const { data, error } = await supabase?.from('performance_stats')?.insert([snakeCaseData])?.select()?.single();
    
    if (error) throw error;
    return { data: this.toCamelCase(data) };
  },

  async update(id, statsData) {
    const snakeCaseData = this.toSnakeCase(statsData);
    const { data, error } = await supabase?.from('performance_stats')?.update(snakeCaseData)?.eq('id', id)?.select()?.single();
    
    if (error) throw error;
    return { data: this.toCamelCase(data) };
  },

  toCamelCase(obj) {
    if (!obj) return obj;
    return {
      id: obj?.id,
      playerId: obj?.player_id,
      season: obj?.season,
      competitionType: obj?.competition_type,
      appearances: obj?.appearances,
      gamesStarted: obj?.games_started,
      gamesSubstitute: obj?.games_substitute,
      minutesPlayed: obj?.minutes_played,
      goalsScored: obj?.goals_scored,
      assists: obj?.assists,
      shotsOnTarget: obj?.shots_on_target,
      bigChancesCreated: obj?.big_chances_created,
      keyPasses: obj?.key_passes,
      yellowCards: obj?.yellow_cards,
      redCards: obj?.red_cards,
      foulsCommitted: obj?.fouls_committed,
      foulsSuffered: obj?.fouls_suffered,
      motmAwards: obj?.motm_awards,
      teamOfWeek: obj?.team_of_week,
      playerOfMonth: obj?.player_of_month,
      createdAt: obj?.created_at,
      updatedAt: obj?.updated_at
    };
  },

  toSnakeCase(obj) {
    if (!obj) return obj;
    return {
      player_id: obj?.playerId,
      season: obj?.season,
      competition_type: obj?.competitionType,
      appearances: obj?.appearances,
      games_started: obj?.gamesStarted,
      games_substitute: obj?.gamesSubstitute,
      minutes_played: obj?.minutesPlayed,
      goals_scored: obj?.goalsScored,
      assists: obj?.assists,
      shots_on_target: obj?.shotsOnTarget,
      big_chances_created: obj?.bigChancesCreated,
      key_passes: obj?.keyPasses,
      yellow_cards: obj?.yellowCards,
      red_cards: obj?.redCards,
      fouls_committed: obj?.foulsCommitted,
      fouls_suffered: obj?.foulsSuffered,
      motm_awards: obj?.motmAwards,
      team_of_week: obj?.teamOfWeek,
      player_of_month: obj?.playerOfMonth
    };
  }
};

// Transfer History Service
export const transferHistoryService = {
  async getByPlayerId(playerId) {
    const { data, error } = await supabase?.from('transfer_history')?.select('*')?.eq('player_id', playerId)?.order('transfer_date', { ascending: false });
    
    if (error) throw error;
    return { data: data?.map(item => this.toCamelCase(item)) };
  },

  async create(transferData) {
    const snakeCaseData = this.toSnakeCase(transferData);
    const { data, error } = await supabase?.from('transfer_history')?.insert([snakeCaseData])?.select()?.single();
    
    if (error) throw error;
    return { data: this.toCamelCase(data) };
  },

  toCamelCase(obj) {
    if (!obj) return obj;
    return {
      id: obj?.id,
      playerId: obj?.player_id,
      fromClub: obj?.from_club,
      toClub: obj?.to_club,
      transferDate: obj?.transfer_date,
      transferFee: obj?.transfer_fee,
      transferType: obj?.transfer_type,
      currency: obj?.currency,
      createdAt: obj?.created_at,
      updatedAt: obj?.updated_at
    };
  },

  toSnakeCase(obj) {
    if (!obj) return obj;
    return {
      player_id: obj?.playerId,
      from_club: obj?.fromClub,
      to_club: obj?.toClub,
      transfer_date: obj?.transferDate,
      transfer_fee: obj?.transferFee,
      transfer_type: obj?.transferType,
      currency: obj?.currency
    };
  }
};

// National Team Service
export const nationalTeamService = {
  async getSeniorByPlayerId(playerId) {
    const { data, error } = await supabase?.from('national_team_senior')?.select('*')?.eq('player_id', playerId)?.single();
    
    if (error && error?.code !== 'PGRST116') throw error;
    return { data: data ? this.toCamelCase(data) : null };
  },

  async getYouthByPlayerId(playerId) {
    const { data, error } = await supabase?.from('national_team_youth')?.select('*')?.eq('player_id', playerId);
    
    if (error) throw error;
    return { data: data?.map(item => this.toCamelCase(item)) };
  },

  async createSenior(teamData) {
    const snakeCaseData = this.toSnakeCase(teamData);
    const { data, error } = await supabase?.from('national_team_senior')?.insert([snakeCaseData])?.select()?.single();
    
    if (error) throw error;
    return { data: this.toCamelCase(data) };
  },

  async createYouth(teamData) {
    const snakeCaseData = this.toSnakeCase(teamData);
    const { data, error } = await supabase?.from('national_team_youth')?.insert([snakeCaseData])?.select()?.single();
    
    if (error) throw error;
    return { data: this.toCamelCase(data) };
  },

  toCamelCase(obj) {
    if (!obj) return obj;
    return {
      id: obj?.id,
      playerId: obj?.player_id,
      youthLevel: obj?.youth_level,
      yearsActive: obj?.years_active,
      caps: obj?.caps,
      goals: obj?.goals,
      competitionsPlayed: obj?.competitions_played,
      debutDate: obj?.debut_date,
      totalCaps: obj?.total_caps,
      totalGoals: obj?.total_goals,
      majorTournamentsPlayed: obj?.major_tournaments_played,
      createdAt: obj?.created_at,
      updatedAt: obj?.updated_at
    };
  },

  toSnakeCase(obj) {
    if (!obj) return obj;
    return {
      player_id: obj?.playerId,
      youth_level: obj?.youthLevel,
      years_active: obj?.yearsActive,
      caps: obj?.caps,
      goals: obj?.goals,
      competitions_played: obj?.competitionsPlayed,
      debut_date: obj?.debutDate,
      total_caps: obj?.totalCaps,
      total_goals: obj?.totalGoals,
      major_tournaments_played: obj?.majorTournamentsPlayed
    };
  }
};

// Medical Records Service
export const medicalRecordsService = {
  async getByPlayerId(playerId) {
    const { data, error } = await supabase?.from('medical_records')?.select('*')?.eq('player_id', playerId)?.order('injury_start_date', { ascending: false });
    
    if (error) throw error;
    return { data: data?.map(item => this.toCamelCase(item)) };
  },

  async create(medicalData) {
    const snakeCaseData = this.toSnakeCase(medicalData);
    const { data, error } = await supabase?.from('medical_records')?.insert([snakeCaseData])?.select()?.single();
    
    if (error) throw error;
    return { data: this.toCamelCase(data) };
  },

  async update(id, medicalData) {
    const snakeCaseData = this.toSnakeCase(medicalData);
    const { data, error } = await supabase?.from('medical_records')?.update(snakeCaseData)?.eq('id', id)?.select()?.single();
    
    if (error) throw error;
    return { data: this.toCamelCase(data) };
  },

  async delete(id) {
    const { error } = await supabase?.from('medical_records')?.delete()?.eq('id', id);
    
    if (error) throw error;
    return { success: true };
  },

  toCamelCase(obj) {
    if (!obj) return obj;
    return {
      id: obj?.id,
      playerId: obj?.player_id,
      injuryType: obj?.injury_type,
      injuryDescription: obj?.injury_description,
      injuryStartDate: obj?.injury_start_date,
      injuryEndDate: obj?.injury_end_date,
      matchesMissed: obj?.matches_missed,
      fitnessStatus: obj?.fitness_status,
      medicalReportFile: obj?.medical_report_file,
      createdAt: obj?.created_at,
      updatedAt: obj?.updated_at
    };
  },

  toSnakeCase(obj) {
    if (!obj) return obj;
    return {
      player_id: obj?.playerId,
      injury_type: obj?.injuryType,
      injury_description: obj?.injuryDescription,
      injury_start_date: obj?.injuryStartDate,
      injury_end_date: obj?.injuryEndDate,
      matches_missed: obj?.matchesMissed,
      fitness_status: obj?.fitnessStatus,
      medical_report_file: obj?.medicalReportFile
    };
  }
};

// Advanced Performance Data Service
export const advancedPerformanceService = {
  async getByPlayerId(playerId) {
    const { data, error } = await supabase?.from('advanced_performance_data')?.select('*')?.eq('player_id', playerId)?.order('season', { ascending: false });
    
    if (error) throw error;
    return { data: data?.map(item => this.toCamelCase(item)) };
  },

  async create(performanceData) {
    const snakeCaseData = this.toSnakeCase(performanceData);
    const { data, error } = await supabase?.from('advanced_performance_data')?.insert([snakeCaseData])?.select()?.single();
    
    if (error) throw error;
    return { data: this.toCamelCase(data) };
  },

  async update(id, performanceData) {
    const snakeCaseData = this.toSnakeCase(performanceData);
    const { data, error } = await supabase?.from('advanced_performance_data')?.update(snakeCaseData)?.eq('id', id)?.select()?.single();
    
    if (error) throw error;
    return { data: this.toCamelCase(data) };
  },

  async delete(id) {
    const { error } = await supabase?.from('advanced_performance_data')?.delete()?.eq('id', id);
    
    if (error) throw error;
    return { success: true };
  },

  toCamelCase(obj) {
    if (!obj) return obj;
    return {
      id: obj?.id,
      playerId: obj?.player_id,
      season: obj?.season,
      passAccuracyPercent: obj?.pass_accuracy_percent,
      dribblesCompleted: obj?.dribbles_completed,
      duelsWonPercent: obj?.duels_won_percent,
      aerialDuelsWon: obj?.aerial_duels_won,
      tacklesMade: obj?.tackles_made,
      interceptions: obj?.interceptions,
      distanceCoveredKm: obj?.distance_covered_km,
      sprintCount: obj?.sprint_count,
      createdAt: obj?.created_at,
      updatedAt: obj?.updated_at
    };
  },

  toSnakeCase(obj) {
    if (!obj) return obj;
    return {
      player_id: obj?.playerId,
      season: obj?.season,
      pass_accuracy_percent: obj?.passAccuracyPercent,
      dribbles_completed: obj?.dribblesCompleted,
      duels_won_percent: obj?.duelsWonPercent,
      aerial_duels_won: obj?.aerialDuelsWon,
      tackles_made: obj?.tacklesMade,
      interceptions: obj?.interceptions,
      distance_covered_km: obj?.distanceCoveredKm,
      sprint_count: obj?.sprintCount
    };
  }
};

// Competitions Played Service
export const competitionsPlayedService = {
  async getByPlayerId(playerId) {
    const { data, error } = await supabase?.from('competitions_played')?.select('*')?.eq('player_id', playerId)?.order('season', { ascending: false });
    
    if (error) throw error;
    return { data: data?.map(item => this.toCamelCase(item)) };
  },

  async create(competitionData) {
    const snakeCaseData = this.toSnakeCase(competitionData);
    const { data, error } = await supabase?.from('competitions_played')?.insert([snakeCaseData])?.select()?.single();
    
    if (error) throw error;
    return { data: this.toCamelCase(data) };
  },

  async update(id, competitionData) {
    const snakeCaseData = this.toSnakeCase(competitionData);
    const { data, error } = await supabase?.from('competitions_played')?.update(snakeCaseData)?.eq('id', id)?.select()?.single();
    
    if (error) throw error;
    return { data: this.toCamelCase(data) };
  },

  async delete(id) {
    const { error } = await supabase?.from('competitions_played')?.delete()?.eq('id', id);
    
    if (error) throw error;
    return { success: true };
  },

  toCamelCase(obj) {
    if (!obj) return obj;
    return {
      id: obj?.id,
      playerId: obj?.player_id,
      season: obj?.season,
      competitionName: obj?.competition_name,
      competitionType: obj?.competition_type,
      appearances: obj?.appearances,
      goals: obj?.goals,
      assists: obj?.assists,
      minutesPlayed: obj?.minutes_played,
      createdAt: obj?.created_at,
      updatedAt: obj?.updated_at
    };
  },

  toSnakeCase(obj) {
    if (!obj) return obj;
    return {
      player_id: obj?.playerId,
      season: obj?.season,
      competition_name: obj?.competitionName,
      competition_type: obj?.competitionType,
      appearances: obj?.appearances,
      goals: obj?.goals,
      assists: obj?.assists,
      minutes_played: obj?.minutesPlayed
    };
  }
};

// National Team Youth Service
export const nationalTeamYouthService = {
  async getByPlayerId(playerId) {
    const { data, error } = await supabase?.from('national_team_youth')?.select('*')?.eq('player_id', playerId);
    
    if (error) throw error;
    return { data: data?.map(item => this.toCamelCase(item)) };
  },

  async create(youthData) {
    const snakeCaseData = this.toSnakeCase(youthData);
    const { data, error } = await supabase?.from('national_team_youth')?.insert([snakeCaseData])?.select()?.single();
    
    if (error) throw error;
    return { data: this.toCamelCase(data) };
  },

  async update(id, youthData) {
    const snakeCaseData = this.toSnakeCase(youthData);
    const { data, error } = await supabase?.from('national_team_youth')?.update(snakeCaseData)?.eq('id', id)?.select()?.single();
    
    if (error) throw error;
    return { data: this.toCamelCase(data) };
  },

  async delete(id) {
    const { error } = await supabase?.from('national_team_youth')?.delete()?.eq('id', id);
    
    if (error) throw error;
    return { success: true };
  },

  toCamelCase(obj) {
    if (!obj) return obj;
    return {
      id: obj?.id,
      playerId: obj?.player_id,
      youthLevel: obj?.youth_level,
      yearsActive: obj?.years_active,
      caps: obj?.caps,
      goals: obj?.goals,
      competitionsPlayed: obj?.competitions_played,
      createdAt: obj?.created_at,
      updatedAt: obj?.updated_at
    };
  },

  toSnakeCase(obj) {
    if (!obj) return obj;
    return {
      player_id: obj?.playerId,
      youth_level: obj?.youthLevel,
      years_active: obj?.yearsActive,
      caps: obj?.caps,
      goals: obj?.goals,
      competitions_played: obj?.competitionsPlayed
    };
  }
};

// National Team Senior Service
export const nationalTeamSeniorService = {
  async getByPlayerId(playerId) {
    const { data, error } = await supabase?.from('national_team_senior')?.select('*')?.eq('player_id', playerId)?.single();
    
    if (error && error?.code !== 'PGRST116') throw error;
    return { data: data ? this.toCamelCase(data) : null };
  },

  async create(seniorData) {
    const snakeCaseData = this.toSnakeCase(seniorData);
    const { data, error } = await supabase?.from('national_team_senior')?.insert([snakeCaseData])?.select()?.single();
    
    if (error) throw error;
    return { data: this.toCamelCase(data) };
  },

  async update(id, seniorData) {
    const snakeCaseData = this.toSnakeCase(seniorData);
    const { data, error } = await supabase?.from('national_team_senior')?.update(snakeCaseData)?.eq('id', id)?.select()?.single();
    
    if (error) throw error;
    return { data: this.toCamelCase(data) };
  },

  toCamelCase(obj) {
    if (!obj) return obj;
    return {
      id: obj?.id,
      playerId: obj?.player_id,
      debutDate: obj?.debut_date,
      totalCaps: obj?.total_caps,
      totalGoals: obj?.total_goals,
      majorTournamentsPlayed: obj?.major_tournaments_played,
      createdAt: obj?.created_at,
      updatedAt: obj?.updated_at
    };
  },

  toSnakeCase(obj) {
    if (!obj) return obj;
    return {
      player_id: obj?.playerId,
      debut_date: obj?.debutDate,
      total_caps: obj?.totalCaps,
      total_goals: obj?.totalGoals,
      major_tournaments_played: obj?.majorTournamentsPlayed
    };
  }
};

// Player Media Service
export const playerMediaService = {
  async getByPlayerId(playerId) {
    const { data, error } = await supabase?.from('player_media')?.select('*')?.eq('player_id', playerId)?.order('updated_at', { ascending: false });
    
    if (error) throw error;
    return { data: data?.map(item => this.toCamelCase(item)) };
  },

  async create(mediaData) {
    const snakeCaseData = this.toSnakeCase(mediaData);
    const { data, error } = await supabase?.from('player_media')?.insert([snakeCaseData])?.select()?.single();
    
    if (error) throw error;
    return { data: this.toCamelCase(data) };
  },

  async update(id, mediaData) {
    const snakeCaseData = this.toSnakeCase(mediaData);
    const { data, error } = await supabase?.from('player_media')?.update(snakeCaseData)?.eq('id', id)?.select()?.single();
    
    if (error) throw error;
    return { data: this.toCamelCase(data) };
  },

  async delete(id) {
    const { error } = await supabase?.from('player_media')?.delete()?.eq('id', id);
    
    if (error) throw error;
    return { success: true };
  },

  toCamelCase(obj) {
    if (!obj) return obj;
    return {
      id: obj?.id,
      playerId: obj?.player_id,
      mediaType: obj?.media_type,
      title: obj?.title,
      description: obj?.description,
      filePath: obj?.file_path,
      fileSize: obj?.file_size,
      mimeType: obj?.mime_type,
      isPublic: obj?.is_public,
      createdAt: obj?.created_at,
      updatedAt: obj?.updated_at
    };
  },

  toSnakeCase(obj) {
    if (!obj) return obj;
    return {
      player_id: obj?.playerId,
      media_type: obj?.mediaType,
      title: obj?.title,
      description: obj?.description,
      file_path: obj?.filePath,
      file_size: obj?.fileSize,
      mime_type: obj?.mimeType,
      is_public: obj?.isPublic
    };
  }
};

// Market Value History Service
export const marketValueHistoryService = {
  async getByPlayerId(playerId) {
    const { data, error } = await supabase?.from('market_value_history')?.select('*')?.eq('player_id', playerId)?.order('valuation_date', { ascending: false });
    
    if (error) throw error;
    return { data: data?.map(item => this.toCamelCase(item)) };
  },

  async create(valueData) {
    const snakeCaseData = this.toSnakeCase(valueData);
    const { data, error } = await supabase?.from('market_value_history')?.insert([snakeCaseData])?.select()?.single();
    
    if (error) throw error;
    return { data: this.toCamelCase(data) };
  },

  async update(id, valueData) {
    const snakeCaseData = this.toSnakeCase(valueData);
    const { data, error } = await supabase?.from('market_value_history')?.update(snakeCaseData)?.eq('id', id)?.select()?.single();
    
    if (error) throw error;
    return { data: this.toCamelCase(data) };
  },

  async delete(id) {
    const { error } = await supabase?.from('market_value_history')?.delete()?.eq('id', id);
    
    if (error) throw error;
    return { success: true };
  },

  toCamelCase(obj) {
    if (!obj) return obj;
    return {
      id: obj?.id,
      playerId: obj?.player_id,
      valuationDate: obj?.valuation_date,
      marketValue: obj?.market_value,
      currency: obj?.currency,
      createdAt: obj?.created_at
    };
  },

  toSnakeCase(obj) {
    if (!obj) return obj;
    return {
      player_id: obj?.playerId,
      valuation_date: obj?.valuationDate,
      market_value: obj?.marketValue,
      currency: obj?.currency
    };
  }
};